import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { action, makeObservable, observable } from 'mobx';
import { getRoot } from 'mobx-easy';
import { AsyncOperationStatusPayload, AsyncOperationStatusType, Modify } from '@bringg/types';
import i18next from 'i18next';
import { Failure } from '@bringg-frontend/batch-action-status';
import { AsyncOperationStore, AsyncOperationStoreOptions } from '@bringg-frontend/global-stores';
import { batchGetFields, TaskFields } from '@bringg-frontend/shared-tasks-data';

import RootStore from 'bringg-web/stores/root-store';
import { workflowsRootStore } from '../stores/internal';
import { workflowsProvider } from '../data-providers/workflows-provider';

type AsyncOperationPayload = { task_ids: number[] };
export type AsyncOperationData = AsyncOperationStatusPayload<AsyncOperationPayload>;

export const FAILED_TASK_FIELDS: TaskFields = ['external_id'];

type Options = {
	numberOfActionsPerTask: number;
	onClose: (store: TasksAsyncOperationStore) => void;
} & AsyncOperationStoreOptions;

export class TasksAsyncOperationStore extends AsyncOperationStore<Failure<true>> {
	private readonly numberOfActionsPerTask: number;

	partialCompleted = new Map<number, number>();
	private onClose: Options['onClose'];

	private constructor({ name, taskIds, requestId, numberOfActionsPerTask, timeout, onClose }: Options) {
		super({
			name,
			taskIds,
			requestId,
			timeout
		});
		makeObservable(this, {
			partialCompleted: observable,
			startAsyncOperation: action
		});

		this.numberOfActionsPerTask = numberOfActionsPerTask || 1;
		this.onClose = onClose;
	}

	static async create(
		options: Modify<Options, { workflowId: number; numberOfActionsPerTask?: never }>
	): Promise<TasksAsyncOperationStore> {
		const numberOfActionsPerTask = await TasksAsyncOperationStore.getNumberOfActionsPerTask(
			options.workflowId,
			options.requestId
		);

		return new TasksAsyncOperationStore({
			...options,
			numberOfActionsPerTask
		});
	}

	dispose() {
		this.realtimeAsyncOperationAbortController.abort();
		this.onClose(this);

		// Make sure we don't call onClose twice
		this.onClose = () => undefined;
	}

	startAsyncOperation(): this {
		// We get realtime updates per each action per task

		this.setupAsyncOperationTimeout(async pendingTasks => {
			await this.onTaskFailure(pendingTasks);
		});

		getRootEnv().dashboardSdk.sdk.asyncOperationStatus.addListener<AsyncOperationPayload>({
			requestId: this.requestId,
			handler: async payload => {
				const taskIds = payload.payload.task_ids;
				if (payload.status === AsyncOperationStatusType.SUCCESS) {
					this.onTaskSuccess(taskIds);
				}

				if (payload.status === AsyncOperationStatusType.FAILURE) {
					await this.onTaskFailure(taskIds);
				}

				if (this.fullyCompleted.size >= this.taskIds.length) {
					this.realtimeAsyncOperationAbortController.abort();
				}
			},

			signal: this.realtimeAsyncOperationAbortController.signal
		});

		return this;
	}

	private static async getNumberOfActionsPerTask(workflowId: number, requestId: string) {
		let workflow = workflowsRootStore.getStore().workflowRepo.get(workflowId);

		if (!workflow) {
			await workflowsProvider.loadAll();
			workflow = workflowsRootStore.getStore().workflowRepo.get(workflowId);
		}

		if (!workflow) {
			console.error('workflow not found', { workflowId, requestId: requestId });
			return 1;
		}

		return workflow.actions.actions.length;
	}

	private onTaskSuccess(taskIds: number[]) {
		const notFinishedTaskIds = taskIds.filter(taskId => !this.fullyCompleted.has(taskId));

		for (const taskId of notFinishedTaskIds) {
			const currentCount = (this.partialCompleted.get(taskId) || 0) + 1;

			if (currentCount === this.numberOfActionsPerTask) {
				this.fullyCompleted.set(taskId, true);
				this.partialCompleted.delete(taskId);
			} else {
				this.partialCompleted.set(taskId, currentCount);
			}
		}
	}

	private async onTaskFailure(taskIds: number[]) {
		const newTaskIds = taskIds.filter(taskId => !this.failuresMap.has(taskId));
		if (!newTaskIds.length) {
			return;
		}

		let tasks: { id: number; external_id: string }[];

		try {
			tasks = (await getRoot<RootStore>().data.tasksStore.loadMany(
				newTaskIds,
				batchGetFields(FAILED_TASK_FIELDS)
			)) as {
				id: number;
				external_id: string;
			}[];
		} catch (error) {
			console.error('failed to load task', error);
			tasks = newTaskIds.map(id => ({ id, external_id: 'unknown' }));
		}

		this.markTasksAsFailed(tasks);
	}

	private markTasksAsFailed(tasks: { id: number; external_id: string; reason?: string }[]) {
		for (const task of tasks) {
			this.failuresMap.set(task.id, {
				id: task.id,
				title: `${i18next.t('DISPATCH_LIST.ORDER')} ${task?.external_id}`,
				reason: undefined
			});

			// Once failed we don't care about the task anymore
			this.fullyCompleted.set(task.id, false);
		}
	}
}
